// API Configuration for Aletheia Admin Frontend


// API Service class
class ApiService {
  private baseUrl: string;
  private token: string | null;
  private environment: 'development' | 'staging' | 'production';

  constructor() {
    this.environment = this.detectEnvironment();
    this.baseUrl = this.getBaseUrl();
    this.token = localStorage.getItem('aletheia_token');
  }

  // Detect current environment based on URL and environment variables
  private detectEnvironment(): 'development' | 'staging' | 'production' {
    // Check for explicit environment override
    const explicitEnv = process.env.REACT_APP_CURRENT_ENVIRONMENT;
    if (explicitEnv === 'staging') {
      console.log('Using explicit staging environment from REACT_APP_CURRENT_ENVIRONMENT');
      return 'staging';
    } else if (explicitEnv === 'production') {
      console.log('Using explicit production environment from REACT_APP_CURRENT_ENVIRONMENT');
      return 'production';
    }
    
    // Detect from current URL (prioritize hostname detection)
    const hostname = window.location.hostname;
    if (hostname.includes('admin-staging.aletheiatech.ai')) {
      console.log('Detected staging environment from hostname:', hostname);
      return 'staging';
    } else if (hostname.includes('admin.aletheiatech.ai')) {
      console.log('Detected production environment from hostname:', hostname);
      return 'production';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('Detected development environment from hostname:', hostname);
      return 'development';
    }
    
    // Fall back to NODE_ENV but prefer staging for deployed environments
    const nodeEnv = process.env.NODE_ENV;
    console.log('NODE_ENV:', nodeEnv, 'REACT_APP_CURRENT_ENVIRONMENT:', explicitEnv, 'hostname:', hostname);
    
    if (nodeEnv === 'production') {
      // If NODE_ENV is production but hostname doesn't match known production domains,
      // assume this is a staging deployment with production build
      console.log('NODE_ENV is production but hostname not recognized - defaulting to staging');
      return 'staging';
    } else {
      console.log('Using development environment (default)');
      return 'development';
    }
  }

  // Set environment and update base URL
  setEnvironment(env: 'development' | 'staging' | 'production') {
    this.environment = env;
    this.baseUrl = this.getBaseUrl();
  }

  // Get current environment
  getEnvironment() {
    return this.environment;
  }

  // Get base URL based on current environment
  private getBaseUrl(): string {
    switch (this.environment) {
      case 'development':
        return process.env.REACT_APP_ALETHEIA_API_URL || 'http://localhost:5001';
      case 'staging':
        return process.env.REACT_APP_ALETHEIA_STAGING_API_URL || 'https://admin-staging.aletheiatech.ai';
      case 'production':
        return process.env.REACT_APP_ALETHEIA_PROD_API_URL || 'https://admin.aletheiatech.ai';
      default:
        return process.env.REACT_APP_ALETHEIA_API_URL || 'http://localhost:5001';
    }
  }

  // Set authentication token
  setToken(token: string) {
    this.token = token;
    localStorage.setItem('aletheia_token', token);
  }

  // Clear authentication token
  clearToken() {
    this.token = null;
    localStorage.removeItem('aletheia_token');
  }

  // Get authentication headers
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // Public convenience methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Generic request method
  public async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific authentication errors for login endpoint
        if (endpoint === '/api/auth/login') {
          if (response.status === 404) {
            throw new Error('Account not found');
          } else if (response.status === 401) {
            throw new Error('Invalid email or password');
          } else if (response.status === 403) {
            throw new Error('Account is disabled or suspended');
          } else if (response.status === 429) {
            throw new Error('Too many login attempts. Please try again later.');
          } else if (response.status === 500) {
            throw new Error('Server error. Please try again later.');
          }
        }
        
        // Handle 401 for other endpoints (token expired)
        if (response.status === 401) {
          this.clearToken();
          throw new Error('Authentication required');
        }
        
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Authentication
  async login(email: string, password: string) {
    try {
      const response = await this.request<{
        success: boolean;
        access_token: string;
        user: {
          id: string;
          email: string;
          role: string;
        };
      }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.success) {
        this.setToken(response.access_token);
      }

      return response;
    } catch (error: any) {
      // Handle network errors and other specific cases
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      if (error.name === 'AbortError') {
        throw new Error('Request was cancelled. Please try again.');
      }
      
      // Re-throw the error with the specific message we set in the request method
      throw error;
    }
  }

  // Validate current token and get user info
  async validateToken() {
    try {
      const response = await this.request<{
        success: boolean;
        user: {
          id: string;
          email: string;
          role: string;
          first_name?: string;
          last_name?: string;
          display_name?: string;
        };
      }>('/api/auth/validate', {
        method: 'GET',
      });

      return response;
    } catch (error: any) {
      // If validation fails, clear the token
      this.clearToken();
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    return this.request<{
      status: string;
      service: string;
      timestamp: string;
      proptrak_api: string;
    }>('/health');
  }

  // Analytics endpoints
  async getAnalyticsDashboard(timeRange: string = '7d') {
    return this.request<{
      pageViews: number;
      uniqueVisitors: number;
      bounceRate: number;
      avgSessionDuration: number;
      topPages: Array<{
        page: string;
        views: number;
        uniqueViews: number;
        avgTimeOnPage: number;
      }>;
      topEvents: Array<{
        event: string;
        count: number;
        conversionRate: number;
      }>;
      conversions: Array<{
        type: string;
        count: number;
        value: number;
      }>;
    }>(`/api/analytics/dashboard?timeRange=${timeRange}`);
  }

  // Blog Management
  async getBlogPosts(queryParams?: string) {
    const endpoint = queryParams ? `/api/blog/admin/posts?${queryParams}` : '/api/blog/admin/posts';
    return this.request<{
      posts: any[];
      total: number;
      pages: number;
      current_page: number;
      per_page: number;
      has_next: boolean;
      has_prev: boolean;
    }>(endpoint);
  }

  async createBlogPost(postData: any) {
    return this.request('/api/blog/admin/posts', {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  async updateBlogPost(postId: string, postData: any) {
    return this.request(`/api/blog/admin/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(postData),
    });
  }

  async deleteBlogPost(postId: string) {
    return this.request(`/api/blog/admin/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  async uploadBlogImage(file: File) {
    const formData = new FormData();
    formData.append('image', file);

    const url = `${this.baseUrl}/api/blog/admin/upload-image`;
    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Upload failed: ${response.statusText}`);
    }

    return response.json();
  }

  async getBlogCategories() {
    return this.request<any[]>('/api/blog/admin/categories');
  }

  async createBlogCategory(categoryData: any) {
    return this.request('/api/blog/admin/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async getBlogTags() {
    return this.request<any[]>('/api/blog/admin/tags');
  }

  async createBlogTag(tagData: any) {
    return this.request('/api/blog/admin/tags', {
      method: 'POST',
      body: JSON.stringify(tagData),
    });
  }

  // Admin Users
  async getAdminUsers() {
    return this.request<{ users: any[] }>('/api/admin/users');
  }

  // PropTrak User Management (for admin portal)
  async getUsers() {
    return this.request<any[]>('/api/users');
  }

  async getUserById(id: number | string) {
    return this.request<any>(`/api/users/${id}`);
  }

  async createUser(userData: any) {
    return this.request('/api/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id: number | string, userData: any) {
    return this.request(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id: number | string) {
    return this.request(`/api/users/${id}`, {
      method: 'DELETE',
    });
  }

  async getCompanies() {
    return this.request<any[]>('/api/companies');
  }

  // Company Management (PropTrak)
  async getProptrakCompany(id: string) {
    return this.request<any>(`/api/proptrak/companies/${id}`);
  }

  async updateProptrakCompany(id: string, data: any) {
    return this.request<any>(`/api/proptrak/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async suspendProptrakCompany(id: string) {
    return this.request<any>(`/api/proptrak/companies/${id}/suspend`, {
      method: 'POST'
    });
  }

  async activateProptrakCompany(id: string) {
    return this.request<any>(`/api/proptrak/companies/${id}/activate`, {
      method: 'POST'
    });
  }

  async createProptrakSubscription(
    companyId: string, 
    plan: string, 
    interval: string, 
    paymentMethodId?: string, 
    trialDays: number = 0, 
    unitCount: number = 0,
    additionalUsers: number = 0,
    additionalStorageGb: number = 0,
    promoCode: string = ''
  ) {
    return this.request<any>(`/api/proptrak/companies/${companyId}/subscription`, {
      method: 'POST',
      body: JSON.stringify({ 
        plan, 
        interval, 
        payment_method_id: paymentMethodId, 
        trial_days: trialDays,
        unit_count: unitCount,
        additional_users: additionalUsers,
        additional_storage_gb: additionalStorageGb,
        promo_code: promoCode
      })
    });
  }

  async getProptrakPaymentMethods(companyId: string) {
    return this.request<any[]>(`/api/proptrak/companies/${companyId}/billing/payment-methods`);
  }

  async createProptrakSetupIntent(companyId: string) {
    return this.request<{ client_secret: string }>(`/api/proptrak/companies/${companyId}/billing/setup-intent`, {
      method: 'POST'
    });
  }

  async setProptrakDefaultPaymentMethod(companyId: string, paymentMethodId: string) {
    return this.request<any>(`/api/proptrak/companies/${companyId}/billing/payment-methods/${paymentMethodId}/default`, {
      method: 'POST'
    });
  }

  async deleteProptrakPaymentMethod(companyId: string, paymentMethodId: string) {
    return this.request<any>(`/api/proptrak/companies/${companyId}/billing/payment-methods/${paymentMethodId}`, {
      method: 'DELETE'
    });
  }

  async adjustProptrakLimits(companyId: string, data: { limit_type: string, adjustment_amount: number, is_free: boolean, reason?: string }) {
    return this.request<any>(`/api/proptrak/companies/${companyId}/adjust-limits`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getCompanyAuditLogs(companyId: string) {
    return this.request<any[]>(`/api/proptrak/companies/${companyId}/audit-logs`);
  }

  async getCompanyTenants(companyId: string) {
    return this.request<any[]>(`/api/proptrak/companies/${companyId}/tenants`);
  }

  async cancelCompanySubscription(companyId: string) {
    return this.request(`/api/proptrak/companies/${companyId}/billing/cancel`, {
      method: 'POST'
    });
  }

  async addCompanyCredits(companyId: string, amount: number, description: string) {
    return this.request(`/api/proptrak/companies/${companyId}/billing/credits`, {
      method: 'POST',
      body: JSON.stringify({ amount, description })
    });
  }

  async manageCompanyTrial(companyId: string, extendDays?: number, endDate?: string) {
    return this.request(`/api/proptrak/companies/${companyId}/billing/trial`, {
      method: 'POST',
      body: JSON.stringify({ extend_days: extendDays, end_date: endDate })
    });
  }

  async changeCompanyBillingCycle(companyId: string, interval: 'month' | 'year') {
    return this.request(`/api/proptrak/companies/${companyId}/billing/cycle`, {
      method: 'POST',
      body: JSON.stringify({ interval })
    });
  }

  // Dashboard metrics
  async getDashboardMetrics() {
    return this.request<any>('/api/dashboard/metrics');
  }

  async getSystemHealth() {
    return this.request<any>('/api/dashboard/health');
  }

  async getBillingData() {
    return this.request<any>('/api/dashboard/billing');
  }

  // Admin Billing & Revenue
  async getBillingStats() {
    return this.get<any>('/api/proptrak/billing/stats');
  }

  async listSubscriptions(page: number = 1) {
    return this.get<any>(`/api/proptrak/billing/subscriptions?page=${page}`);
  }

  async getPromoChains() {
    return this.get<any>('/api/proptrak/billing/promo-chains');
  }

  async createPromoChain(data: any) {
    return this.post<any>('/api/proptrak/billing/promo-chains', data);
  }

  async deletePromoChain(chainId: string) {
    return this.delete<any>(`/api/proptrak/billing/promo-chains/${chainId}`);
  }

  async getProptrakPaymentHistory(companyId: string) {
    return this.get<any[]>(`/api/proptrak/companies/${companyId}/billing/invoices`);
  }
}

// Legacy API exports for backward compatibility
export const userAPI = {
  getUsers: () => apiService.getUsers(),
  getUserById: (id: number) => apiService.getUserById(id),
  createUser: (userData: any) => apiService.createUser(userData),
  updateUser: (id: number, userData: any) => apiService.updateUser(id, userData),
  deleteUser: (id: number) => apiService.deleteUser(id),
  getCompanies: () => apiService.getCompanies(),
};

export const dashboardAPI = {
  getMetrics: () => apiService.getDashboardMetrics(),
  getSystemHealth: () => apiService.getSystemHealth(),
  getBillingData: () => apiService.getBillingData(),
};

export const analyticsAPI = {
  getDashboard: (timeRange: string) => apiService.getAnalyticsDashboard(timeRange),
};

export const blogAPI = {
  getPosts: async (page: number = 1, perPage: number = 10, status?: string) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      if (status && status !== 'all') {
        params.append('status', status);
      }
      const response = await apiService.getBlogPosts(params.toString());
      return { success: true, ...response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  createPost: async (postData: any) => {
    try {
      const response = await apiService.createBlogPost(postData);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  updatePost: async (postId: string, postData: any) => {
    try {
      const response = await apiService.updateBlogPost(postId, postData);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  deletePost: async (postId: string) => {
    try {
      const response = await apiService.deleteBlogPost(postId);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  uploadImage: async (file: File, postId?: number) => {
    try {
      const response = await apiService.uploadBlogImage(file);
      return { success: true, url: response.url || response.image_url };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  getCategories: async () => {
    try {
      const response = await apiService.getBlogCategories();
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  createCategory: async (categoryData: any) => {
    try {
      const response = await apiService.createBlogCategory(categoryData);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  getTags: async () => {
    try {
      const response = await apiService.getBlogTags();
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
  createTag: async (tagData: any) => {
    try {
      const response = await apiService.createBlogTag(tagData);
      return { success: true, data: response };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
};

// Export singleton instance
export const apiService = new ApiService();
export default apiService; 